import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import { Buffer } from "buffer";
import he from "he";
import React from "react";
import parse from "html-react-parser";
import { Location, NavigateFunction } from "react-router-dom";
import SpeechClassComponent from "../QuestionSpeech";
import Header from "../SharedComponents/Header/Header";
import LoginGuard, { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { withLocation } from "../SharedComponents/withLocation";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import { ConsentController, ConsentControllerDelegate, IConsentTerm } from "./controller";

interface State {
  error: {
    message?: string;
  };
  data: {
    termTitle: string;
    termDescription: string;
    assignmentToken: string;
    answeredConsent: boolean;
    acceptedTerms: boolean;
    instrumentId: string;
    allow_audio: boolean;
    type: string;
  };
  isLoading: boolean;
}

interface Props {
  locationHooks: Location;
  navigationHooks: NavigateFunction
}

export class Consent extends React.Component<Props, State> implements ConsentControllerDelegate {

  private controller: ConsentController;

  constructor(props: Props) {
    super(props);
    this.controller = new ConsentController(this);
    this.state = {
      error: {},
      data: {
        termTitle: "",
        termDescription: "",
        assignmentToken: this.props.locationHooks.state.data.assignmentToken,
        answeredConsent: this.props.locationHooks.state.data.answeredConsent,
        instrumentId: this.props.locationHooks.state.data.instrumentId,
        acceptedTerms: false,
        allow_audio: this.props.locationHooks.state.data.allow_audio,
        type: this.props.locationHooks.state.data.type
      },
      isLoading: true
    };
  }

  componentDidMount(): void {
    this.controller.getConsent(
      localStorage.getItem("AccessToken")!,
      this.state.data.assignmentToken
    )
  }

  // Delegate
  genericError(message?: string): void {
    this.setState({
      error: {
        message: message || "Não foi possível carregar o instrumento. Tente novamente.",
      },
      isLoading: false,
    });
  }

  applicationShouldLogout(): void {
    logout();
  }

  didGetConsentTerm(term: IConsentTerm): void {
    this.setState({
      data: {
        ...this.state.data,
        termDescription: term.description,
        termTitle: term.title,
        allow_audio: term.allow_audio,
        type: term.type
      },
      isLoading: false
    });
  }

  didGetAssignmentToken(token: string): void {

    const splitToken = token.split(".")[1];
    const tokenData = JSON.parse(Buffer.from(splitToken, "base64").toString("utf8"));
    this.setState({
      data: {
        ...this.state.data,
        assignmentToken: token,
        answeredConsent: tokenData.answeredConsent
      }
    });
  }

  didAnswerConsent(): void {

    const mountedStateData = {
      assignmentToken: this.state.data.assignmentToken,
      answeredConsent: this.state.data.answeredConsent,
      totalQuestions: this.props.locationHooks.state.data.totalQuestions,
      instrumentTitle: this.props.locationHooks.state.data.instrumentTitle,
      instrumentId: this.props.locationHooks.state.data.instrumentId,
      applicationId: this.props.locationHooks.state.data.applicationId,
      type: this.props.locationHooks.state.data.type
    };

    this.props.navigationHooks("/instrument/questions", {
      state: {
        data: mountedStateData
      }
    });
  }

  // Internals
  didClickButton(event: any) {
    this.controller.answerConsent(
      localStorage.getItem("AccessToken")!,
      this.state.data.assignmentToken,
      this.state.data.acceptedTerms
    )
  }

  didChangeCheckbox(event: any) {
    this.setState({
      data: {
        ...this.state.data,
        acceptedTerms: event.target.checked
      }
    })
  }

  renderLoading() {
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
      >
        <CircularProgress />
      </Box>
    );
  }

  renderError() {
    if (!this.state.error.message) {
      return null;
    }

    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={4}
        mx={2}
        width="100%"
        maxWidth={600}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {this.state.error.message}
        </Alert>
      </Box>
    );
  }


  renderSpeechTextAnswer(text: string, auto?: boolean, css?: object | null) {

    return (
      <React.Fragment>
        <SpeechClassComponent text={text} auto={auto} css={css}/>
      </React.Fragment>
    );
  }

  cleanHtmlText(html: string | null | undefined) {
    // termDescription is nullable at runtime (the DB column is nullable and the
    // `string` State type lies). Guard before .replace() — this runs BEFORE the
    // null-safe parse() at the render site, so without it a null term white-screens.
    const textWithoutTags = (html ?? "").replace(/<[^>]*>/g, "");
    return he.decode(textWithoutTags);
  }
  renderConsent() {
    const htmlToText = this.cleanHtmlText(this.state.data.termDescription)
    const audioTermDescription = this.state.data.allow_audio ? this.renderSpeechTextAnswer(htmlToText, false, {display: 'inline'}) : null;
    return (
      <Box
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginTop={2}
        marginX={10}
      >
        <Typography marginTop={8} variant="h3" color="primary">
          {this.state.data.termTitle}
        </Typography>
        <Box marginTop={2}>
          {audioTermDescription ? audioTermDescription: null }
          {parse(this.state.data.termDescription ?? "")}
        </Box>
        <FormControlLabel
          label="Aceito os termos"
          sx={{
            marginTop: 5
          }}
          control={
            <Checkbox onChange={(event) => this.didChangeCheckbox(event)} />
          }
        />
        <Button
          variant="contained"
          onClick={(event) => this.didClickButton(event)}
          sx={{
            borderRadius: 10,
            marginTop: 1,
            width: {
              xs: 300,
              sm: 300,
              md: 300
            },
            alignSelf: "center"
          }}
        >
          <Typography color="white" variant="button">
            {this.state.data.acceptedTerms ? `Aceitar e Continuar` : `Rejeitar e Continuar`}
          </Typography>
        </Button>
      </Box>
    )
  }

  render() {
    return (
      <Box display="flex" flexDirection="column" width="100%" alignItems="center" justifyContent="center">
        <LoginGuard />
        <Header logoutEnabled={true} />
        {this.state.isLoading
          ? this.renderLoading()
          : this.state.error.message
            ? this.renderError()
            : this.renderConsent()}
      </Box>
    )
  }
}

export default withNavigationHooks(
  withLocation(
    Consent
  )
);
